/**
 * Shared layout for the two public legal pages (Privacy Policy + Terms).
 * Renders a minimalist header (brand + back-to-home link), the policy title,
 * effective date, an intro paragraph and a numbered list of sections. Content
 * is pulled from `legalContent.js` in the visitor's current language.
 */
import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Globe2 } from "lucide-react";
import { useBranding } from "@/context/BrandingContext";
import { useLandingContent } from "@/context/LandingContentContext";
import { getLegalContent } from "./legalContent";

export default function LegalPage({ kind /* "privacy" | "terms" */ }) {
  const { t, i18n } = useTranslation();
  const { brand } = useBranding();
  const cms = useLandingContent();
  const bundled = getLegalContent(i18n.language, kind);

  // If the MASTER_ADMIN has typed content in the CMS for this page + language,
  // use it. Otherwise fall back to the bundled locale defaults.
  const content = useMemo(() => {
    const cmsEntry = (cms?.legalPages?.[kind] || {})[i18n.language]
      || (cms?.legalPages?.[kind] || {}).en;
    if (cmsEntry?.body && cmsEntry.body.trim().length > 0) {
      // Split blank-line-separated paragraphs into "sections" so the layout
      // renders consistently. A heading line (starts with "## ") begins a
      // new titled section; otherwise it stays in the intro/current section.
      const paragraphs = cmsEntry.body.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
      const sections = [];
      let currentSection = null;
      let intro = "";
      for (const p of paragraphs) {
        if (p.startsWith("## ")) {
          currentSection = { title: p.slice(3).trim(), body: [] };
          sections.push(currentSection);
        } else if (currentSection) {
          currentSection.body.push(p);
        } else if (!intro) {
          intro = p;
        } else {
          // No heading yet — collect leading paragraphs as a numbered section.
          if (!sections.length) sections.push({ title: "", body: [] });
          sections[0].body.push(p);
        }
      }
      return {
        heading: cmsEntry.title || bundled.heading,
        effective: cmsEntry.updated_at
          ? `${t("landing.legal_last_updated", "Last updated")}: ${new Date(cmsEntry.updated_at).toLocaleDateString()}`
          : bundled.effective,
        intro: intro || bundled.intro,
        sections: sections.length ? sections : bundled.sections,
        _fromCms: true,
      };
    }
    return bundled;
  }, [cms, kind, i18n.language, bundled, t]);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.title = `${content.heading} — ${brand?.brand_name || "My GlobalTopup"}`;
      window.scrollTo({ top: 0 });
    }
  }, [content.heading, brand?.brand_name, kind]);

  return (
    <div className="min-h-screen bg-white text-slate-900" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* Simple header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2"
            data-testid="legal-brand"
          >
            <div className="w-9 h-9 rounded-lg bg-[#0F62FE] text-white flex items-center justify-center overflow-hidden">
              {brand?.brand_logo_url ? (
                <img src={brand.brand_logo_url} alt={brand.brand_name} className="w-full h-full object-contain" />
              ) : (
                <Globe2 className="w-5 h-5" strokeWidth={2.2} />
              )}
            </div>
            <div className="font-bold text-base sm:text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {brand?.brand_name || "My GlobalTopup"}
            </div>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors"
            data-testid="legal-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("landing.legal_back_home", "Back to home")}
          </Link>
        </div>
      </header>

      {/* Hero band */}
      <div className="relative bg-gradient-to-br from-[#0F62FE] via-[#0038B8] to-[#001A66] text-white overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-10"
        >
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-white blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-cyan-300 blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/80">
            {kind === "privacy"
              ? t("landing.footer_privacy", "Privacy Policy")
              : t("landing.footer_terms", "Terms & Conditions")}
          </div>
          <h1
            className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1]"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            data-testid="legal-heading"
          >
            {content.heading}
          </h1>
          <div className="mt-4 text-white/70 text-sm" data-testid="legal-effective">
            {content.effective}
          </div>
        </div>
      </div>

      {/* Body */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
        <p className="text-lg text-slate-700 leading-relaxed" data-testid="legal-intro">
          {content.intro}
        </p>

        <div className="mt-12 space-y-10">
          {content.sections.map((s, i) => (
            <section key={i} data-testid={`legal-section-${i}`}>
              <h2
                className="text-xl sm:text-2xl font-bold text-slate-900"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              >
                {s.title}
              </h2>
              <div className="mt-3 space-y-3">
                {s.body.map((p, j) => (
                  <p key={j} className="text-slate-600 leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Footer inside the page: cross-link to the other legal page */}
        <div className="mt-16 pt-8 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
          <Link
            to={kind === "privacy" ? "/terms" : "/privacy"}
            className="text-[#0F62FE] font-semibold hover:underline"
            data-testid="legal-crosslink"
          >
            {kind === "privacy"
              ? t("landing.footer_terms", "Terms & Conditions")
              : t("landing.footer_privacy", "Privacy Policy")} →
          </Link>
          <div className="text-slate-500">
            © {new Date().getFullYear()} {brand?.brand_name || "My GlobalTopup"}. {t("landing.footer_rights", "All rights reserved.")}
          </div>
        </div>
      </main>
    </div>
  );
}
